/**
 * Variant generation.
 *
 * ADDRESSING — a variant is a 32-bit seed, decomposed as:
 *
 *   geo   = seed % GEO_VARIANTS[kind]        -> changes GEOMETRY
 *           (helmet type, pauldron size, armour tier, build/height bucket,
 *            shield, beard, greaves)
 *   tint  = (seed / 16 | 0) % TINT_COUNT     -> changes VERTEX COLOUR only
 *           (sash hue, cloth value, skin tone, plume colour)
 *   wpn   = (seed / 512 | 0) % weapons(kind) -> which weapon is attached
 *
 * Because tint rides on vertex colour and faction rides on the material, the
 * crowd renderer only ever needs GEO_VARIANTS[kind] distinct buffers per LOD:
 * a 500-man army is 8 grunt meshes x 12 colourways x 5 weapons = 480 readable
 * combinations for the price of 8 geometry uploads.
 */
import * as THREE from 'three';
import { PROPORTIONS } from './rig.js';
import { RNG } from '../core/rng.js';
import { planarUV, tileUV, paint, indexed } from './geom.js';

export const GEO_VARIANTS = { grunt: 8, archer: 3, sergeant: 3, officer: 2, general: 1, hero: 1, player: 1 };
export const TINT_COUNT = 12;
export const WEAPONS_FOR = {
  grunt: ['spear', 'dao', 'halberd', 'axe', 'spear'],
  archer: ['bow'],
  sergeant: ['halberd', 'dao', 'spear'],
  officer: ['dao', 'halberd', 'greatGlaive'],
  general: ['greatGlaive', 'halberd'],
  hero: ['greatGlaive'],
  player: ['greatGlaive'],
};

/** total distinct visual combinations addressable per kind */
export function variantCountFor(kind) {
  return (GEO_VARIANTS[kind] || 1) * TINT_COUNT * (WEAPONS_FOR[kind] || ['dao']).length;
}
export const VARIANT_COUNT = variantCountFor('grunt');

/* --------------------------------------------------------------- colourways */

const SASH = [
  [1.00, 1.00, 1.00], [1.12, 0.84, 0.48], [0.70, 0.86, 1.12], [1.06, 0.60, 0.52],
  [0.78, 1.06, 0.76], [1.14, 1.02, 0.70], [0.90, 0.78, 1.10], [1.16, 0.70, 0.38],
  [0.62, 0.70, 0.80], [1.00, 0.92, 0.80], [0.84, 0.94, 0.98], [1.08, 0.88, 0.62],
];
const CLOTHV = [1.00, 0.86, 1.10, 0.78, 0.94, 1.04, 0.82, 1.12, 0.90, 1.00, 0.88, 1.06];
const SKINV = [
  [1.00, 1.00, 1.00], [0.90, 0.86, 0.82], [1.08, 1.02, 0.96], [0.82, 0.76, 0.70],
  [1.02, 0.96, 0.90], [0.94, 0.90, 0.88], [1.06, 0.98, 0.90], [0.86, 0.82, 0.80],
  [0.98, 0.94, 0.92], [1.04, 1.00, 0.98], [0.90, 0.84, 0.76], [1.00, 0.96, 0.92],
];
const PLUME = [
  [1.30, 0.42, 0.30], [1.25, 1.05, 0.45], [0.45, 0.70, 1.30], [1.10, 1.10, 1.10],
  [0.55, 1.15, 0.65], [1.35, 0.70, 0.25], [0.85, 0.45, 1.20], [1.20, 0.30, 0.45],
  [1.15, 0.95, 0.60], [0.60, 0.95, 1.15], [1.30, 0.55, 0.20], [0.95, 0.95, 1.20],
];

const C = (a, k = 1) => new THREE.Color().setRGB(a[0] * k, a[1] * k, a[2] * k);
const V = (v) => new THREE.Color().setRGB(v, v, v);

function tintSet(ti, tier) {
  const sash = SASH[ti % SASH.length];
  const cv = CLOTHV[(ti * 5 + 3) % CLOTHV.length];
  const sk = SKINV[(ti * 7 + 1) % SKINV.length];
  const pl = PLUME[(ti * 3 + 2) % PLUME.length];
  const lamV = 0.90 + ((ti * 11) % 7) / 7 * 0.24 + tier * 0.06;
  return {
    lam: V(lamV),
    lamHi: V(lamV * 1.10),
    cloth: V(cv),
    trouser: V(cv * 0.82),
    sleeve: V(cv * 0.94),
    sash: C(sash),
    trim: V(0.90 + tier * 0.035),
    leather: V(0.92 + ((ti * 13) % 5) / 5 * 0.20),
    steel: V(0.96 + tier * 0.08),
    iron: V(0.90 + ((ti * 17) % 4) / 4 * 0.22),
    skin: C(sk),
    hair: V(0.95),
    cape: V(1.0 + tier * 0.05),
    plume: C(pl),
  };
}

/* ------------------------------------------------------------------- specs */

const HELMETS_GRUNT = ['cap', 'conical', 'cap', 'wrap', 'topknot', 'conical', 'horned', 'cap'];

/**
 * Deterministically derive the full build spec for (kind, seed).
 * `geoOnly` variants (0..GEO_VARIANTS-1) address geometry; everything else is
 * colour, so two specs with the same geo index share a buffer.
 */
export function specFor(kind, seed = 0) {
  const K = PROPORTIONS[kind] ? kind : 'grunt';
  const gv = GEO_VARIANTS[K] || 1;
  const gi = ((seed % gv) + gv) % gv;
  const ti = (((seed / 16) | 0) % TINT_COUNT + TINT_COUNT) % TINT_COUNT;
  const wl = WEAPONS_FOR[K] || ['dao'];
  const wi = (((seed / 512) | 0) % wl.length + wl.length) % wl.length;
  const rng = new RNG(0x9e37 ^ (K.length * 7919) ^ (gi * 104729));
  const P = PROPORTIONS[K];

  const tier = K === 'general' ? 3 : K === 'officer' ? 2 : K === 'sergeant' ? 1 : K === 'hero' || K === 'player' ? 3 : 0;
  const S = {
    kind: K, geo: gi, tintIndex: ti, weapon: wl[wi], tier,
    height: P.height, build: P.build, bulk: P.bulk, legs: P.legs, neck: P.neck,
    cuirass: 'lamellar', collar: false, sash: true, tassets: 6, tassetLen: 0.76,
    greave: true, greaveMat: 'steel', bracer: true, bracerMat: 'leather',
    sleeve: 'cloth', pauldron: 'small', pauldronL: null, pauldronR: null,
    pauldronProfile: 'square', straps: false, beard: 0,
    helmet: 'cap', helmMat: 'iron', plume: false, cape: 0, shield: false,
    capeNotch: 0, capeSweep: 0.052, headProfile: 'field',
    tint: tintSet(ti, tier),
  };

  if (K === 'grunt' || K === 'archer') {
    // Build/height jitter across geo variants so a crowd has a real size
    // spread — a rank whose heads all sit at one height reads as one mesh
    // repeated. ~±0.11 m of height and ±0.10 of build, plus leg-length jitter.
    const hj = [-0.085, 0.045, -0.030, 0.100, 0.010, -0.110, 0.070, -0.055][gi % 8];
    const bj = [-0.02, 0.09, -0.06, 0.04, -0.035, 0.11, 0.015, -0.075][gi % 8];
    const lj = [0.02, -0.015, 0.03, -0.02, 0.01, 0.025, -0.03, 0.0][gi % 8];
    S.height = P.height + hj;
    S.build = P.build + bj;
    S.bulk = P.bulk + bj * 0.8;
    S.legs = P.legs + lj;
    S.helmet = K === 'archer' ? ['wrap', 'cap', 'topknot'][gi % 3] : HELMETS_GRUNT[gi % HELMETS_GRUNT.length];
    // Each geometry bucket owns a different MACRO shoulder grammar. The live
    // crowd only addresses grunt geo 0/1/3/6, so those four must remain
    // distinguishable after texture, tint and pose have disappeared.
    S.pauldron = (gi % 4 === 1 || gi % 4 === 2) ? 'large' : 'small';
    const paulProfiles = ['square', 'shielded', 'stepped', 'banner', 'square', 'stepped', 'swept', 'square'];
    S.pauldronProfile = paulProfiles[gi % paulProfiles.length];
    if (gi === 1) {                       // dao: shield side open, sword side armoured
      S.pauldronL = 'small';
      S.pauldronR = 'large';
    } else if (gi === 3) {                // banner: a lean cloth-led outline
      S.pauldronL = 'none';
      S.pauldronR = 'small';
    } else if (gi === 6) {                // halberd: swept single high plate
      S.pauldronL = 'small';
      S.pauldronR = 'large';
    }
    S.plume = gi % 3 === 1;
    S.cuirass = gi === 5 ? 'cloth' : 'lamellar';
    S.tassets = 4 + (gi % 3);
    S.tassetLen = 0.80 - (gi % 3) * 0.025;
    S.greave = gi % 2 === 0;
    S.beard = gi % 4 === 3 ? 1 : 0;
    S.straps = gi % 3 === 1;
    S.shield = K === 'grunt' && gi === 1;
    S.collar = gi === 2 || gi === 7;
    S.helmMat = gi % 3 === 2 ? 'steel' : 'iron';
  } else if (K === 'sergeant') {
    S.helmet = ['crested', 'horned', 'conical'][gi % 3];
    S.pauldron = gi === 2 ? 'large' : 'winged';
    S.pauldronProfile = gi === 1 ? 'swept' : 'stepped';
    S.plume = true;
    S.collar = true;
    S.straps = true;
    S.beard = 1;
    S.cape = gi === 0 ? 1 : 0;
    S.tassetLen = 0.74;
    S.build = P.build + (gi === 1 ? 0.05 : 0);
    S.height = P.height + (gi === 1 ? 0.03 : 0);
    S.greaveMat = 'steel';
    S.bracerMat = 'steel';
  } else if (K === 'officer') {
    S.helmet = gi === 0 ? 'crested' : 'lord';
    S.pauldron = 'large';
    S.pauldronProfile = gi === 0 ? 'stepped' : 'shielded';
    S.plume = true;
    S.collar = true;
    S.straps = true;
    S.beard = gi === 1 ? 2 : 0;
    S.cape = 1;
    S.tassets = 6;
    S.tassetLen = 0.72;
    S.greaveMat = 'steel';
    S.bracerMat = 'steel';
    S.helmMat = 'steel';
    S.sleeve = 'silk';
    S.tint.sleeve = new THREE.Color().setRGB(0.92, 0.90, 0.84);
  } else if (K === 'general') {
    S.helmet = 'lord';
    S.pauldron = 'winged';
    S.pauldronProfile = 'stepped';
    S.plume = true;
    S.collar = true;
    S.straps = true;
    S.beard = 2;
    S.cape = 2;
    S.tassets = 6;
    S.tassetLen = 0.68;
    S.greaveMat = 'steel';
    S.bracerMat = 'steel';
    S.helmMat = 'steel';
    S.sleeve = 'silk';
    S.tint.sleeve = new THREE.Color().setRGB(0.98, 0.95, 0.88);
  } else if (K === 'hero' || K === 'player') {
    S.helmet = 'topknot';
    S.pauldron = 'winged';
    // The weapon side stays compact while the opposite sode supplies the named
    // silhouette. Both still share one merged body buffer and one material.
    S.pauldronL = 'winged';
    S.pauldronR = 'small';
    S.pauldronProfile = 'hero';
    S.plume = true;
    S.collar = true;
    S.straps = true;
    S.beard = 0;
    S.cape = 2;
    S.capeNotch = 0.16;
    S.capeSweep = 0.086;
    S.headProfile = 'heroSwept';
    S.tassets = 6;
    S.tassetLen = 0.70;
    S.greaveMat = 'steel';
    S.bracerMat = 'steel';
    // A pale under-robe on the sleeves breaks the all-indigo read and makes
    // the arms separate from the cuirass at gameplay distance.
    S.sleeve = 'silk';
    S.tint = tintSet(0, 3);
    // Still a pale silk role, but restrained enough that normal exposure does
    // not merge both arms into the face/skin value band.
    S.tint.sleeve = new THREE.Color().setRGB(0.92, 0.90, 0.84);
    // Uniformly lower the gold-silk value while preserving its hue. The same
    // role feeds the forehead band and waist sash, both of which entered the
    // skin highlight band under the authored sun/ACES exposure.
    S.tint.sash = new THREE.Color().setRGB(0.88, 0.72, 0.42);
    S.tint.plume = new THREE.Color().setRGB(1.3, 0.4, 0.3);
  }
  return S;
}

/**
 * The per-instance colour multiplier for a seed, for crowd renderers that
 * instance the shared `chars.lods[kind]` buffers. Those buffers are baked with
 * colourway 0, so multiplying by this in `InstancedMesh.instanceColor`
 * reproduces the same 12 colourways `meshFor()` bakes into vertex colour.
 * Returns `out` (a THREE.Color, linear space).
 */
export function variantTint(seed, out = new THREE.Color()) {
  const ti = (((seed / 16) | 0) % TINT_COUNT + TINT_COUNT) % TINT_COUNT;
  const cv = CLOTHV[(ti * 5 + 3) % CLOTHV.length];
  const lamV = 0.90 + ((ti * 11) % 7) / 7 * 0.24;
  const k = (lamV * 0.6 + cv * 0.4);
  const sash = SASH[ti % SASH.length];
  // bias slightly toward the sash hue so neighbouring units read differently
  return out.setRGB(k * (0.88 + sash[0] * 0.12), k * (0.88 + sash[1] * 0.12), k * (0.88 + sash[2] * 0.12));
}

/** Colourway index for a seed (0..TINT_COUNT-1). */
export function tintIndexFor(seed) {
  return (((seed / 16) | 0) % TINT_COUNT + TINT_COUNT) % TINT_COUNT;
}

/* ------------------------------------------------------------ LOD3 cutout */

/**
 * Flat billboard-ready silhouette. ~30 triangles, no skinning: the crowd
 * renderer instances this at long range and can yaw it to face the camera.
 * The outline is the thing that matters — helmet crest, pauldrons wider than
 * the hips, flared tassets, split legs.
 */
export function flatSilhouette(S, uvRects) {
  const h = S.height / 1.80;
  const big = S.pauldron === 'large' || S.pauldron === 'winged';
  const sw = (big ? 0.375 : 0.340) * (S.build || 1);
  const crest = (S.helmet === 'crested' || S.helmet === 'lord' ? 0.20 : S.helmet === 'topknot' ? 0.09 : 0.05)
    + (S.plume ? 0.14 : 0.04);
  const tl = S.tassetLen;
  // right half, top -> bottom (x >= 0)
  const half = [
    [0.020, 1.80 + crest],
    [0.055, 1.79],
    [0.110, 1.76],
    [0.122, 1.70],
    [0.150, 1.665],
    [0.100, 1.632],
    [0.140, 1.600],
    [sw * 0.60, 1.588],
    [sw, 1.520],
    [sw * 0.97, 1.412],
    [0.232, 1.392],
    [0.214, 1.30],
    [0.178, 1.08],
    [0.196, 1.00],
    [0.246, tl + 0.03],
    [0.205, tl - 0.01],
    [0.150, tl - 0.05],
    [0.132, 0.44],
    [0.140, 0.16],
    [0.165, 0.035],
    [0.170, 0.000],
    [0.028, 0.000],
    [0.030, 0.40],
  ];
  const pts = [];
  for (const [x, y] of half) pts.push([x * h, y * h]);
  pts.push([0.0, (0.46) * h]);
  for (let i = half.length - 1; i >= 0; i--) {
    const [x, y] = half[i];
    pts.push([-x * h, y * h]);
  }
  const shape = new THREE.Shape();
  shape.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
  shape.closePath();
  let g = new THREE.ShapeGeometry(shape, 1);
  g = indexed(g);
  planarUV(g, 'x', 'y');
  tileUV(g, uvRects.lamellar, { su: 0.5, sv: 0.5, ou: 0.25, ov: 0.25 });
  const t = S.tint?.lam || new THREE.Color(1, 1, 1);
  paint(g, t, (x, y) => 0.72 + 0.34 * Math.min(1, y / (1.4 * h)));
  g.computeBoundingSphere();
  return g;
}
